import { Input, Skeleton } from "antd";
import { SearchOutlined, CloseOutlined } from "@ant-design/icons";
import { useState, useMemo } from "react";
import "./GlobalSearchBar.css";
import GlobalSearchBarFunctionalitues from "./GlobalSearchBarFunctions";
import LazyObligationDetails from "components/universal/ObligationDetails/LazyObligationDetails";
import FrameworkCircles from "components/universal/FrameworkCircles/FrameworkCircles";
import { useTranslation } from "react-i18next";

const STATUS_BADGE_CONFIG = {
  completed: {
    bg: "#BFD9C0",
    text: "#297A2B",
    border: "#BFD9C0",
  },
  completedLate: {
    bg: "#F1D9BF",
    text: "#C7842C",
    border: "#F1D9BF",
  },
  overdue: {
    bg: "#E1C4C6",
    text: "#852A2E",
    border: "#E1C4C6",
  },
  inprogress: {
    bg: "#BCCEE5",
    text: "#093E7D",
    border: "#BCCEE5",
  },
  scheduled: {
    bg: "#E6E6E6",
    text: "#999999",
    border: "#E6E6E6",
  },
};

const STATUS_BADGE_ORDER = [
  { type: "completed", key: "completedCount", fallbackKey: "completed" },
  { type: "completedLate", key: "completedLateCount", fallbackKey: "completed_late" },
  { type: "overdue", key: "lateCount", fallbackKey: "late" },
  { type: "inprogress", key: "workingOnCount", fallbackKey: "working_on" },
  { type: "scheduled", key: "scheduledCount", fallbackKey: "scheduled" },
];

const getStatusBadgeStyle = (type) => {
  const t = String(type || "");
  if (STATUS_BADGE_CONFIG[t]) return STATUS_BADGE_CONFIG[t];
  return { bg: "#f5f5f5", text: "#374151", border: "#d9d9d9" };
};

const buildStatusBadges = (groupStatus = {}) =>
  STATUS_BADGE_ORDER.map(({ type, key, fallbackKey }) => {
    const raw = groupStatus[key] ?? groupStatus[fallbackKey] ?? 0;
    const count = Number(raw) || 0;
    return { type, count, display: count > 0 ? String(count) : "-" };
  });

const getReadableTextColor = (hex, fallback = "#ffffff") => {
  if (!hex) return fallback;
  const h = String(hex).replace("#", "").trim();
  const raw = h.length === 8 ? h.slice(0, 6) : h;
  if (raw.length !== 6) return fallback;

  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return fallback;

  const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
  return lum > 0.62 ? "#111827" : "#ffffff";
};

function ResultsSkeleton({ count = 6 }) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="gsb2-row gsb2-row--skeleton" aria-hidden="true">
          <div className="gsb2-row-left">
            <Skeleton
              active
              title={{ width: `${55 + (i % 4) * 10}%` }}
              paragraph={{ rows: 1, width: "88%" }}
            />
          </div>
        </div>
      ))}
    </>
  );
}

export default function GlobalSearchBarDesign() {
  const {
    MIN_CHARS,
    PAGE_SIZE,
    loading,

    open,
    value,
    helpText,
    selectedType,

    wrapperRef,
    panelRef,

    isMac,
    showResults,
    footerText,

    obligationsList,

    visibleObligations,

    hasMoreObligations,

    visibleCountObligations,

    filteredRecents,

    handleOpen,
    handleChange,
    handlePressEnter,
    handleSearchClick,

    setSelectedType,
    setVisibleCountObligations,

    addToRecentlyViewed,

    accessScopeUsers,
    accessScopeFrameworks,
    groupStatusMap,
    selectedFrameworkKeys,
    selectedResponsibleKeys,
    toggleFrameworkKey,
    toggleResponsibleKey,
  } = GlobalSearchBarFunctionalitues();
  const { t } = useTranslation("globalSearch");
  const [drawerRequest, setDrawerRequest] = useState(null);

  const [visibleFrameworkCount, setVisibleFrameworkCount] = useState(5);
  const [visibleResponsibleCount, setVisibleResponsibleCount] = useState(5);

  const visibleFrameworks = useMemo(
    () => (accessScopeFrameworks || []).slice(0, visibleFrameworkCount),
    [accessScopeFrameworks, visibleFrameworkCount]
  );
  const visibleResponsibles = useMemo(
    () => (accessScopeUsers || []).slice(0, visibleResponsibleCount),
    [accessScopeUsers, visibleResponsibleCount]
  );

  const hasMoreFrameworks =
    (accessScopeFrameworks || []).length > visibleFrameworkCount;
  const hasMoreResponsibles =
    (accessScopeUsers || []).length > visibleResponsibleCount;

  const openObligationDrawer = (item) => {
    addToRecentlyViewed(item);
    setDrawerRequest({ id: item.group_id || item.id, k: Date.now() });
  };

  const searchEnabled = value.trim().length >= MIN_CHARS;
  const searchButtonActive = searchEnabled && !loading;

  const obligationOnlyRecents = (filteredRecents || []).filter(
    (x) => x?.type === "Obligations"
  );

  return (
    <div ref={wrapperRef} className="gsb2-wrapper">
      <Input
        id="global-search-input"
        className="gsb2-input"
        style={{ height: 40 }}
        placeholder={t("global_search.placeholders.search_input")}
        prefix={
          <SearchOutlined
            className="gsb2-input-prefixIcon"
            style={{ fontSize: 14 }}
          />
        }
        value={value}
        onChange={handleChange}
        onPressEnter={handlePressEnter}
        onFocus={handleOpen}
        onClick={handleOpen}
        allowClear={{
          clearIcon: (
            <CloseOutlined
              className="gsb2-input-clearIcon"
              style={{ fontSize: 12 }}
            />
          ),
        }}
        suffix={
          <span className="gsb2-suffix" onMouseDown={(e) => e.preventDefault()}>
            {searchEnabled ? (
              <span className="gsb2-enterhint">
                {loading
                  ? t("global_search.hints.searching")
                  : t("global_search.hints.enter_for_search")}
              </span>
            ) : (
              <span className="gsb2-shortcut">
                {isMac ? "⌘ ⇧ K" : "Ctrl ⇧ K"}
              </span>
            )}

            <button
              type="button"
              className="gsb2-search-btn"
              onClick={handleSearchClick}
              disabled={!searchEnabled || loading}
              title={t("global_search.tooltips.search_button_min_chars", {
                min: MIN_CHARS,
              })}
              style={{
                height: 28,
                padding: "0 12px",
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 8,
                background: searchButtonActive ? "#1677ff" : "#f3f4f6",
                color: searchButtonActive ? "#ffffff" : "#98a2b3",
                border: `1px solid ${searchButtonActive ? "#1677ff" : "#d9d9d9"}`,
                cursor: searchButtonActive ? "pointer" : "not-allowed",
              }}
            >
              {loading
                ? t("global_search.hints.searching")
                : t("global_search.buttons.search")}
            </button>
          </span>
        }
      />

      {drawerRequest?.id && (
        <LazyObligationDetails
          key={drawerRequest.k}
          obligationId={drawerRequest.id}
          displayMode="drawer"
          drawerWidth={1800}
          open={true}
          onClose={() => setDrawerRequest(null)}
        />
      )}

      {open && (
        <div ref={panelRef} className="gsb2-panel">
          <div className="gsb2-panel-header">
            <span className="gsb2-panel-title">
              {t("global_search.titles.panel")}
            </span>
            <div className="gsb2-typeRow">
              {[
                {
                  label: t("global_search.labels.type_obligations"),
                  value: "Obligations",
                },
              ].map((opt) => {
                const active =
                  opt.value === "Obligations" &&
                  (selectedType === "Obligations" || selectedType === "All");

                return (
                  <button
                    key={opt.value}
                    type="button"
                    className={`gsb2-pill ${active ? "gsb2-pill--active" : ""}`}
                    onClick={() => {
                      setSelectedType(opt.value);
                      setVisibleCountObligations(PAGE_SIZE);
                    }}
                    aria-pressed={active}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="gsb2-layout">
            <div className="gsb2-main">
              <div className="gsb2-card gsb2-card--left">
                <div className="gsb2-leftTop">
                  {helpText && <div className="gsb2-help">{helpText}</div>}
                  <span className="gsb2-sectionTitle">
                    {showResults
                      ? t("global_search.labels.section_results")
                      : t("global_search.labels.section_recently_viewed")}
                  </span>
                </div>

                <div className="gsb2-leftScroll gsb2-scroll">
                  {showResults ? (
                    <div className="gsb2-list">
                      {(loading || obligationsList.length > 0) && (
                        <>
                          {loading ? (
                            <ResultsSkeleton count={6} />
                          ) : (
                            <>
                              {visibleObligations.map((item) => {
                                const groupStatus =
                                  groupStatusMap?.[item.group_id] || {};

                                const statusBadges = buildStatusBadges(groupStatus);

                                const fwName = (item.framework_name || "").trim();
                                const fwBg = item.framework_bg || "#959595ff";
                                const fwColor =
                                  item.framework_font ||
                                  getReadableTextColor(fwBg, "#ffffff");

                                return (
                                  <button
                                    key={item.id}
                                    type="button"
                                    className="gsb2-row"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      openObligationDrawer(item);
                                    }}
                                  >
                                    <div className="gsb2-row-left">
                                      <div className="gsb2-row-title">
                                        {item.title}
                                      </div>

                                      <div className="gsb2-row-bottom">
                                        <div className="gsb2-row-meta">
                                          {!!item.obl_tag && (
                                            <span className="gsb2-tag gsb2-tag--blue">
                                              {item.obl_tag}
                                            </span>
                                          )}

                                          <FrameworkCircles
                                            frameworks={{
                                              id: fwName || "FW",
                                              name: fwName || "FW",
                                              background_color: fwBg,
                                              font_color: fwColor,
                                            }}
                                            styleOverrides={{
                                              width: 22,
                                              height: 22,
                                              fontSize: 8,
                                              fontWeight: 700,
                                              borderRadius: "50%",
                                              flexShrink: 0,
                                            }}
                                            gap={0}
                                            showTooltips={false}
                                          />

                                          {!!item.due_date && (
                                            <span className="gsb2-date">
                                              {item.due_date}
                                            </span>
                                          )}

                                          {!!item.responsible_name && (
                                            <span className="gsb2-tag gsb2-tag--green">
                                              {item.responsible_name}
                                            </span>
                                          )}
                                        </div>

                                        <div className="gsb2-row-status">
                                          {statusBadges.map((s, idx) => {
                                            const style = getStatusBadgeStyle(
                                              s.type
                                            );

                                            return (
                                              <span
                                                key={idx}
                                                className="gsb2-status"
                                                style={{
                                                  background: style.bg,
                                                  color: style.text,
                                                  borderColor: style.border,
                                                }}
                                              >
                                                {s.display}
                                              </span>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}

                              {hasMoreObligations && !loading && (
                                <div className="gsb2-viewmore-wrap">
                                  <button
                                    type="button"
                                    className="gsb2-viewmore-btn"
                                    onClick={() =>
                                      setVisibleCountObligations(
                                        (n) => n + PAGE_SIZE
                                      )
                                    }
                                  >
                                    {t("global_search.buttons.view_more_count", {
                                      count: Math.min(
                                        PAGE_SIZE,
                                        obligationsList.length -
                                          visibleCountObligations
                                      ),
                                    })}
                                  </button>

                                  <div className="gsb2-viewmore-meta">
                                    {t("global_search.labels.showing_of", {
                                      shown: visibleObligations.length,
                                      total: obligationsList.length,
                                    })}
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}

                      {!loading && obligationsList.length === 0 && (
                        <div className="gsb2-empty">
                          {t("global_search.messages.no_results")}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="gsb2-list">
                      {obligationOnlyRecents.map((item) => {
                        const groupStatus =
                          groupStatusMap?.[item.group_id] || {};

                        const statusBadges = buildStatusBadges(groupStatus);

                        const fwName = (item.framework_name || "").trim();
                        const fwBg = item.framework_bg || "#959595ff";
                        const fwColor =
                          item.framework_font ||
                          getReadableTextColor(fwBg, "#ffffff");

                        return (
                          <button
                            key={`${item.type}-${item.id}`}
                            type="button"
                            className="gsb2-row"
                            onClick={(e) => {
                              e.preventDefault();
                              openObligationDrawer(item);
                            }}
                          >
                            <div className="gsb2-row-left">
                              <div className="gsb2-row-title">{item.title}</div>

                              <div className="gsb2-row-bottom">
                                <div className="gsb2-row-meta">
                                  {!!item.obl_tag && (
                                    <span className="gsb2-tag gsb2-tag--blue">
                                      {item.obl_tag}
                                    </span>
                                  )}

                                  <FrameworkCircles
                                    frameworks={{
                                      id: fwName || "FW",
                                      name: fwName || "FW",
                                      background_color: fwBg,
                                      font_color: fwColor,
                                    }}
                                    styleOverrides={{
                                      width: 22,
                                      height: 22,
                                      fontSize: 8,
                                      fontWeight: 700,
                                      borderRadius: "50%",
                                      flexShrink: 0,
                                    }}
                                    gap={0}
                                    showTooltips={false}
                                  />

                                  {!!item.due_date && (
                                    <span className="gsb2-date">
                                      {item.due_date}
                                    </span>
                                  )}

                                  {!!item.responsible_name && (
                                    <span className="gsb2-tag gsb2-tag--green">
                                      {item.responsible_name}
                                    </span>
                                  )}
                                </div>

                                <div className="gsb2-row-status">
                                  {statusBadges.map((s, idx) => {
                                    const style = getStatusBadgeStyle(s.type);

                                    return (
                                      <span
                                        key={idx}
                                        className="gsb2-status"
                                        style={{
                                          background: style.bg,
                                          color: style.text,
                                          borderColor: style.border,
                                        }}
                                      >
                                        {s.display}
                                      </span>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </button>
                        );
                      })}

                      {obligationOnlyRecents.length === 0 && (
                        <div className="gsb2-empty">
                          {t("global_search.messages.no_recently_viewed")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="gsb2-sidebar">
              <div className="gsb2-card gsb2-card--right">
                <div className="gsb2-rightScroll gsb2-scroll">
                  <div
                    className="gsb2-sectionHeader"
                    style={{ marginTop: "14px" }}
                  >
                    <span className="gsb2-sectionTitle">
                      {t("global_search.labels.filter_by_framework")}
                    </span>
                  </div>

                  <div className="gsb2-filterList">
                    {visibleFrameworks.map((fw) => {
                      const name = fw.name;
                      const bg = fw.background_color || "#959595ff";
                      const key = fw.id || name;

                      return (
                        <label key={key} className="gsb2-filterRow">
                          <input
                            type="checkbox"
                            className="gsb2-filterCheckbox"
                            checked={selectedFrameworkKeys.includes(key)}
                            onChange={() => toggleFrameworkKey(key)}
                          />

                          <FrameworkCircles
                            frameworks={{
                              id: fw.id || name,
                              name: name,
                              background_color: bg,
                              font_color: fw.font_color || "#ffffff",
                            }}
                            styleOverrides={{
                              width: 22,
                              height: 22,
                              fontSize: 8,
                              fontWeight: 700,
                              borderRadius: "50%",
                              flexShrink: 0,
                            }}
                            gap={0}
                            showTooltips={false}
                          />

                          <span className="gsb2-filterLabel">{name}</span>
                        </label>
                      );
                    })}

                    {hasMoreFrameworks && (
                      <button
                        type="button"
                        className="gsb2-filterViewMore"
                        onClick={() => setVisibleFrameworkCount((n) => n + 5)}
                      >
                        {t("global_search.buttons.view_more_count", {
                          count: Math.min(
                            5,
                            (accessScopeFrameworks || []).length -
                              visibleFrameworkCount
                          ),
                        })}
                      </button>
                    )}
                  </div>

                  <div className="gsb2-rightDivider" />

                  <div className="gsb2-sectionHeader">
                    <span className="gsb2-sectionTitle">
                      {t("global_search.labels.filter_by_responsible")}
                    </span>
                  </div>

                  <div className="gsb2-filterList">
                    {visibleResponsibles.map((u) => {
                      const key = u.id || u.name;
                      return (
                        <label key={key} className="gsb2-filterRow">
                          <input
                            type="checkbox"
                            className="gsb2-filterCheckbox"
                            checked={selectedResponsibleKeys.includes(key)}
                            onChange={() => toggleResponsibleKey(key)}
                          />
                          <span className="gsb2-filterLabel">{u.name}</span>
                        </label>
                      );
                    })}

                    {hasMoreResponsibles && (
                      <button
                        type="button"
                        className="gsb2-filterViewMore"
                        onClick={() => setVisibleResponsibleCount((n) => n + 5)}
                      >
                        {t("global_search.buttons.view_more_count", {
                          count: Math.min(
                            5,
                            (accessScopeUsers || []).length -
                              visibleResponsibleCount
                          ),
                        })}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>{" "}
          {showResults && footerText && (
            <div className="gsb2-footer">
              <span className="gsb2-footerText">{footerText}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}